import React, { useState, useMemo } from 'react';
import { RotateCcw } from 'lucide-react';
import { ThemeModal } from './ThemeDialogs';
import { SEED_FIELDS } from '../../../theme/themeGenerator';

// Same names as the builder's seed fields, so a slot reads the same in both places.
export const ASSIGN_SLOTS = ['primary', 'barBg', 'neutralTint', 'surface', 'pageBg', 'text']
  .map(key => SEED_FIELDS.find(f => f.key === key));

const DEFAULT_MAP = ['primary', 'barBg', 'neutralTint'];

const initialAssign = (count) => {
  const a = {};
  ASSIGN_SLOTS.forEach(s => { a[s.key] = null; });
  DEFAULT_MAP.forEach((slot, i) => { if (i < count) a[slot] = i; });
  return a;
};

/**
 * Second step after the harmony picker: hold a harmony chip, then click a seed
 * slot to give it that color. Slots without a chip keep the builder's current
 * value. onApply receives { slotKey: hex } for the assigned slots only.
 */
const HarmonyAssignDialog = ({ colors, current, onBack, onCancel, onApply }) => {
  const [assign, setAssign] = useState(() => initialAssign(colors.length));
  const [held, setHeld] = useState(null);

  const slotOfChip = useMemo(() => {
    const m = {};
    Object.entries(assign).forEach(([slot, chip]) => { if (chip != null) m[chip] = slot; });
    return m;
  }, [assign]);

  const clickChip = (i) => setHeld(h => (h === i ? null : i));

  const clickSlot = (slot) => {
    if (held == null) return;
    setAssign(prev => {
      const next = { ...prev };
      const displaced = prev[slot];
      const heldFrom = Object.keys(prev).find(k => prev[k] === held);
      if (heldFrom) next[heldFrom] = displaced != null && displaced !== held ? displaced : null;
      next[slot] = held;
      return next;
    });
    setHeld(null);
  };

  const resetSlot = (slot) => setAssign(prev => ({ ...prev, [slot]: null }));

  const finish = () => {
    const out = {};
    ASSIGN_SLOTS.forEach(s => { if (assign[s.key] != null) out[s.key] = colors[assign[s.key]]; });
    onApply(out);
  };

  return (
    <ThemeModal
      title="Assign Harmony Colors"
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onBack}>Back</button>
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={finish}>Apply</button>
        </>
      )}
    >
      <div className="stui-label-note">Click a color to hold it, then click a seed slot to assign it. Assigning onto a filled slot swaps.</div>
      <div className="stui-theme-assign-chips">
        {colors.map((hex, i) => (
          <button
            key={i}
            type="button"
            className={`stui-theme-assign-chip${held === i ? ' held' : ''}`}
            onClick={() => clickChip(i)}
            title={hex}
          >
            <span className="stui-theme-assign-swatch" style={{ background: hex }} />
            {slotOfChip[i] ? ASSIGN_SLOTS.find(s => s.key === slotOfChip[i]).label : 'Unassigned'}
          </button>
        ))}
      </div>

      {ASSIGN_SLOTS.map(s => {
        const chip = assign[s.key];
        const shown = chip != null ? colors[chip] : current[s.key];
        return (
          <div key={s.key} className="stui-theme-assign-slot">
            <button
              type="button"
              className={`stui-theme-assign-slot-btn${held != null ? ' armed' : ''}`}
              onClick={() => clickSlot(s.key)}
            >
              <span className="stui-theme-assign-slot-swatch" style={{ background: shown || 'transparent' }} />
              <span className="stui-theme-assign-slot-name">{s.label}</span>
              <span className="stui-sublabel">{chip != null ? `Color ${chip + 1}` : (shown ? 'current' : 'none')}</span>
            </button>
            <button
              type="button"
              className="stui-crud-btn"
              title="Reset to the builder's current value"
              disabled={chip == null}
              onClick={() => resetSlot(s.key)}
            >
              <RotateCcw size={12} />
            </button>
          </div>
        );
      })}
    </ThemeModal>
  );
};

export default HarmonyAssignDialog;
